import React, { forwardRef } from 'react';

import { usePopperTooltip } from 'react-popper-tooltip';

import { formatDateWithTz } from 'Helpers/dateUtils';
import { formatFigure } from 'Helpers/formatService';

export default function ProcurementApprovalsTableTile(props) {
    const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip({ interactive: true });

    return (
        <div
            className="table__card tw-mx-3 tw-my-2 cursor-pointer"
            ref={setTriggerRef}
            onClick={() => {
                props.showProcurementInfoModal(props.item.id);
            }}
        >
            <h5 className="tw-py-2">{props.item.name || $t('Procurement name not specified')}</h5>
            <h5>{props.item.owner_name || $t('No owner')}</h5>

            {props.groupName !== 'notStarted' && visible && (
                <TileTooltip ref={setTooltipRef} procurement={{ item: props.item }} getTooltipProps={getTooltipProps} getArrowProps={getArrowProps} />
            )}
        </div>
    );
}

const TileTooltip = forwardRef(function TileTooltip({ procurement, getTooltipProps, getArrowProps }, ref) {
    return (
        <div
            ref={ref}
            data-popper-reference-hidden="true"
            {...getTooltipProps({
                className:
                    'tw-flex tw-flex-col tw-gap-y-2 tw-bg-white tw-p-3 tw-rounded-lg tw-border-2 tw-border-charcoal-50 tw-opacity-90 tw-w-full tw-z-[999]',
                style: { maxWidth: '1140px' },
            })}
        >
            {!procurement && <div>{$t('Something went wrong')}</div>}
            {!!procurement && (
                <div className="tw-flex tw-flex-col tw-gap-12">
                    <div>
                        <h5 className="table__header tw-mb-2">{procurement.item.name || $t('Procurement name not specified')}</h5>
                    </div>

                    <div>
                        {!!procurement.item.purchase_requisition && (
                            <>
                                <h6 className="table__header tw-mb-2">{$t('Purchase Requisition')}</h6>
                                <div className="inbud-table_wrapper">
                                    <table className="inbud-table">
                                        <thead>
                                            <tr>
                                                <th>{$t('Code')}</th>
                                                <th>{$t('Title')}</th>
                                                <th>{$t('Description')}</th>
                                                <th>{$t('Estimated Value')}</th>
                                                <th>{$t('Contract Date')}</th>
                                                <th>{$t('Status')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>{procurement.item.purchase_requisition.pr_code}</td>
                                                <td>{procurement.item.purchase_requisition.title}</td>
                                                <td>{procurement.item.purchase_requisition.description}</td>
                                                <td>{formatFigure(procurement.item.purchase_requisition.estimated_value)}</td>
                                                <td>
                                                    {procurement.item.purchase_requisition.contract_date
                                                        ? formatDateWithTz(
                                                              procurement.item.purchase_requisition.contract_date,
                                                              'YYYY-MM-DD HH:mm:ss',
                                                              'DD/MM/YYYY',
                                                          )
                                                        : procurement.item.purchase_requisition.contract_date}
                                                </td>
                                                <td>{procurement.item.purchase_requisition.status}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {!!procurement.item.purchase_requisition.approvers && procurement.item.purchase_requisition.approvers.length > 0 && (
                                    <>
                                        <h6 className="table__header tw-mb-2">{$t('Purchase Requisition Approvers')}</h6>
                                        <div className="inbud-table_wrapper">
                                            <ApproversTable approvers={procurement.item.purchase_requisition.approvers} />
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    <div>
                        {!!procurement.item.purchase_order && (
                            <>
                                <h6 className="table__header tw-mb-2">{$t('Expenditure Approval')}</h6>
                                <div className="inbud-table_wrapper">
                                    <table className="inbud-table">
                                        <thead>
                                            <tr>
                                                <th>{$t('Description')}</th>
                                                <th>{$t('Final Price')}</th>
                                                <th>{$t('Variance')}</th>
                                                <th>{$t('Contract Started Date')}</th>
                                                <th>{$t('Contract Ended Date')}</th>
                                                <th>{$t('Status')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>{procurement.item.purchase_order.description}</td>
                                                <td>{formatFigure(procurement.item.purchase_order.final_price)}</td>
                                                <td>{formatFigure(procurement.item.purchase_order.variance)}</td>
                                                <td>
                                                    {procurement.item.purchase_order.contract_started_at
                                                        ? formatDateWithTz(
                                                              procurement.item.purchase_order.contract_started_at,
                                                              'YYYY-MM-DD HH:mm:ss',
                                                              'DD/MM/YYYY',
                                                          )
                                                        : procurement.item.purchase_order.contract_started_at}
                                                </td>
                                                <td>
                                                    {procurement.item.purchase_order.contract_ended_at
                                                        ? formatDateWithTz(
                                                              procurement.item.purchase_order.contract_ended_at,
                                                              'YYYY-MM-DD HH:mm:ss',
                                                              'DD/MM/YYYY',
                                                          )
                                                        : procurement.item.purchase_order.contract_ended_at}
                                                </td>
                                                <td>{procurement.item.purchase_order.status_label}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {!!procurement.item.purchase_order && procurement.item.purchase_order.approvers.length > 0 && (
                                    <>
                                        <h6 className="table__header tw-mb-2">{$t('Expenditure Approval Approvers')}</h6>
                                        <div className="inbud-table_wrapper">
                                            <ApproversTable approvers={procurement.item.purchase_order.approvers} />
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

const ApproversTable = (props) => {
    return (
        <table className="inbud-table">
            <thead>
                <tr>
                    <th>{$t('Name')}</th>
                    <th>{$t('Approved At')}</th>
                    <th>{$t('Comments')}</th>
                </tr>
            </thead>
            <tbody>
                {props.approvers.map((approver) => {
                    return (
                        <tr key={approver.id}>
                            <td>{approver.name}</td>
                            <td>{approver.approved_at ? formatDateWithTz(approver.approved_at, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') : approver.approved_at}</td>
                            <td>{approver.comments}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};
